'use client';

import { formatDistanceToNowStrict } from 'date-fns';
import {
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
} from 'flowbite-react';
import Link from 'next/link';
import { useCallback, useRef } from 'react';
import { NotificationsIcon } from '@/components/icons';
import { customDropdownTheme } from '@/lib/flowbite-theme';
import { trackAppEvent } from '@/lib/posthog';
import { bucketCount } from '@/lib/posthog-events';
import type { Notification } from '@/types/notifications';
import { enrichNotifications } from '@/utils/notificationHelpers';

export default function NotificationsDropdown({
  notifications,
  unreadCount,
  totalCount,
  isMobile,
}: {
  notifications: Notification[];
  unreadCount: number;
  totalCount: number;
  isMobile?: boolean;
}) {
  // Enrich notifications with parsed payload data
  const enrichedNotifications = enrichNotifications(notifications);
  // Show all notifications that were fetched (no slicing)
  const displayedNotifications = enrichedNotifications;

  // Track which notification IDs have been marked as read to avoid duplicate API calls
  const markedAsReadIdsRef = useRef<Set<string>>(new Set());

  // Mark visible notifications as read when dropdown opens
  const handleDropdownOpen = useCallback(() => {
    // Get unread notification IDs from the visible list that haven't been marked yet
    const unreadIds = notifications
      .filter(
        (n) => !n.is_read && !markedAsReadIdsRef.current.has(n.notification_id),
      )
      .map((n) => n.notification_id);

    if (unreadIds.length === 0) return;

    // Add these IDs to the set of marked notifications
    unreadIds.forEach((id) => markedAsReadIdsRef.current.add(id));

    // Mark each notification as read in background (fire and forget)
    unreadIds.forEach((id) => {
      fetch(`/api/notifications/${id}/read`, { method: 'POST' }).catch((err) =>
        console.error('Failed to mark notification as read:', err),
      );
    });
  }, [notifications]);

  return (
    <div>
      <Dropdown
        arrowIcon={false}
        inline
        label={
          <div className="relative">
            <div className="cursor-pointer rounded-lg p-2 text-gray-500 hover:bg-purple-50 focus:outline-none focus:ring-0 dark:text-gray-400 dark:hover:bg-purple-900/30 transition-colors">
              <span className="sr-only">View notifications</span>
              <NotificationsIcon className="h-6 w-6" />
              {unreadCount > 0 && (
                <div
                  className={`absolute -right-1 -top-1 flex items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white shadow-lg ${unreadCount > 99 ? 'h-5 w-auto px-1.5' : 'h-5 w-5'}`}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}
            </div>
          </div>
        }
        onToggle={(isOpen) => {
          if (isOpen) {
            trackAppEvent('notification_dropdown_opened', {
              unread_count_bucket: bucketCount(unreadCount),
            });
            handleDropdownOpen();
          }
        }}
        theme={customDropdownTheme}
      >
        <DropdownHeader>
          <div className="flex items-center justify-between gap-4 w-[420px]">
            <span className="block text-base font-bold text-gray-900 dark:text-white tracking-tight">
              Notifications
            </span>
            {unreadCount > 0 && (
              <span className="rounded-full bg-purple-600 px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm">
                {unreadCount} new
              </span>
            )}
          </div>
        </DropdownHeader>

        <div className="max-h-[400px] overflow-y-auto w-[420px]">
          {notifications.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <NotificationsIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No notifications yet
              </p>
            </div>
          ) : (
            displayedNotifications.map((notification) => {
              const enriched = notification.enriched;
              const hasEnrichedData =
                enriched && Object.keys(enriched).length > 0;

              return (
                <Link
                  className="block"
                  href={
                    enriched?.action_url ||
                    notification.action_url ||
                    '/notifications'
                  }
                  key={notification.notification_id}
                  onClick={() => {
                    trackAppEvent('notification_clicked', {
                      notification_type: notification.type
                        ? String(notification.type)
                        : undefined,
                    });
                  }}
                >
                  <DropdownItem
                    className={`
                    ${!notification.is_read ? 'bg-purple-50/50 dark:bg-purple-900/10' : ''} 
                    cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 
                    border-l-4 
                    ${!notification.is_read ? 'border-l-purple-600' : 'border-l-transparent'}
                    transition-all duration-150
                    !justify-start !text-left
                  `}
                  >
                    <div className="w-full py-0.5 text-left">
                      {/* Title row with unread indicator */}
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-semibold text-sm text-gray-900 dark:text-white line-clamp-1 text-left">
                          {notification.title}
                        </p>
                        {!notification.is_read && (
                          <span className="h-2 w-2 rounded-full bg-purple-600 flex-shrink-0"></span>
                        )}
                      </div>

                      {/* Enriched data - patient name inline */}
                      {hasEnrichedData && enriched?.patient_name && (
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-0.5 text-left">
                          {enriched.patient_name}
                        </p>
                      )}

                      {/* Message */}
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-0.5 text-left whitespace-pre-line">
                        {notification.message.replace(/<[^>]*>/g, '')}
                      </p>

                      {/* Footer row: timestamp and priority */}
                      <div className="flex items-center justify-between gap-2 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400 dark:text-gray-500">
                            {formatDistanceToNowStrict(
                              new Date(notification.created_on),
                              { addSuffix: true },
                            )}
                          </span>
                          {notification.priority === 'critical' && (
                            <span className="text-[11px] font-medium text-red-600 dark:text-red-400">
                              Urgent
                            </span>
                          )}
                          {notification.priority === 'high' && (
                            <span className="text-[11px] font-medium text-orange-600 dark:text-orange-400">
                              High
                            </span>
                          )}
                        </div>
                        {/* Show action button if notification has a deep link */}
                        {(enriched?.action_url || notification.action_url) && (
                          <span className="text-[11px] font-medium text-purple-600 dark:text-purple-400 hover:underline">
                            {notification.action_button_text || 'View →'}
                          </span>
                        )}
                      </div>
                    </div>
                  </DropdownItem>
                </Link>
              );
            })
          )}
        </div>

        {notifications.length > 0 && (
          <>
            <DropdownDivider />
            <Link
              className="block w-full px-4 py-2.5 text-left text-sm font-medium text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/30 transition-colors"
              href="/notifications"
            >
              {totalCount > notifications.length
                ? `View all ${totalCount} notifications →`
                : 'View all notifications →'}
            </Link>
          </>
        )}
      </Dropdown>
    </div>
  );
}
